import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { proXpApi, formatCents } from "@/lib/experienceApi";
import { parisDatetimeLocalToIso, formatParisDate } from "@/lib/dtParis";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Calendar, Plus, ArrowRight, CheckCircle2, AlertCircle, ArrowLeft, Home, Trash2, Pencil } from "lucide-react";
import { auth } from "@/lib/api";
import { api } from "@/lib/api";

export default function ProOrgDetail() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [evName, setEvName] = useState("");
  const [organizerName, setOrganizerName] = useState("");
  const [address, setAddress] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [enableSlots, setEnableSlots] = useState(false);
  const [slotDuration, setSlotDuration] = useState(60);
  const [slotCapacity, setSlotCapacity] = useState(20);
  const isAdmin = auth.getUser()?.role === "admin";

  const markHouse = async () => {
    try { const o = (await api.post(`/admin/experience/orgs/${orgId}/mark-house`)).data;
      setOrg(o); toast.success("Compte maison activé — Stripe geoVINUM"); }
    catch { toast.error("Impossible"); }
  };
  const unmarkHouse = async () => {
    try { const o = (await api.post(`/admin/experience/orgs/${orgId}/unmark-house`)).data;
      setOrg(o); toast.success("Retour Stripe Connect"); }
    catch { toast.error("Impossible"); }
  };

  const load = async () => {
    try {
      const [o, evs] = await Promise.all([proXpApi.getOrg(orgId), proXpApi.listEvents(orgId)]);
      setOrg(o); setEvents(evs);
    } catch { toast.error("Introuvable"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const onboard = async () => {
    try {
      const res = await proXpApi.stripeOnboard(orgId, {
        return_url: window.location.href + "?stripe=done",
        refresh_url: window.location.href + "?stripe=refresh",
      });
      window.location.href = res.onboarding_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Stripe indisponible"); }
  };
  const refreshStripe = async () => {
    try { const s = await proXpApi.stripeStatus(orgId); toast.success(s.charges_enabled ? "Stripe actif !" : "En cours de vérification"); load(); }
    catch { toast.error("Erreur Stripe"); }
  };

  const createEvent = async () => {
    if (!evName || !startsAt || !endsAt) { toast.error("Champs requis"); return; }
    const startsIso = parisDatetimeLocalToIso(startsAt);
    const endsIso = parisDatetimeLocalToIso(endsAt);
    if (!startsIso || !endsIso) { toast.error("Dates invalides"); return; }
    try {
      const ev = await proXpApi.createEvent(orgId, {
        name: evName,
        organizer_display_name: organizerName.trim() || null,
        starts_at: startsIso,
        ends_at: endsIso,
        location: address ? { address } : null,
        languages: ["fr"],
      });
      // Auto-generate slots between starts_at and ends_at when the organizer enables them
      if (enableSlots && slotDuration > 0) {
        try {
          const start = new Date(startsIso);
          const end = new Date(endsIso);
          let cursor = new Date(start);
          const slots = [];
          while (cursor < end) {
            const next = new Date(cursor.getTime() + slotDuration * 60000);
            if (next > end) break;
            slots.push({ starts_at: cursor.toISOString(), ends_at: next.toISOString(), capacity: parseInt(slotCapacity) || 20 });
            cursor = next;
          }
          for (const s of slots) await proXpApi.createSlot(ev.id, s);
          if (slots.length) toast.success(`${slots.length} créneau${slots.length>1?"x":""} créés`);
        } catch { toast.error("Créneaux non générés — vous pouvez les créer manuellement"); }
      }
      setEvents([ev, ...events]); setShowNew(false);
      setEvName(""); setOrganizerName(""); setAddress("");
      setStartsAt(""); setEndsAt(""); setEnableSlots(false);
      nav(`/pro/experience/events/${ev.id}`);
    } catch { toast.error("Impossible"); }
  };

  if (!org) return <div className="p-6 text-center text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/pro/experience" className="text-sm text-neutral-600 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Toutes les organisations
      </Link>
      <h1 className="text-3xl font-display text-[#1F3A5F] mb-1">{org.name}</h1>
      <p className="text-xs text-neutral-500">Commission {(org.default_commission_pct / 100).toFixed(1)}% · {org.country} · {org.currency}</p>

      <Card className="my-4 border-l-4 border-[#E5276A]">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org.is_house ? <Home className="w-8 h-8 text-[#E5276A]" /> : <CreditCard className="w-8 h-8 text-[#E5276A]" />}
            <div>
              <div className="font-semibold text-[#1F3A5F]">
                {org.is_house ? "Compte Stripe maison (geoVINUM)" : "Compte Stripe Connect"}
              </div>
              {org.is_house ? (
                <div className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Paiements sur le compte geoVINUM · aucune commission
                </div>
              ) : org.charges_enabled ? (
                <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Prêt à recevoir des paiements</div>
              ) : (
                <div className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Non configuré ou en attente</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {isAdmin && !org.is_house && (
              <Button size="sm" variant="outline" onClick={markHouse} data-testid="mark-house">
                <Home className="w-3.5 h-3.5 mr-1" /> Compte maison
              </Button>
            )}
            {isAdmin && org.is_house && (
              <Button size="sm" variant="ghost" onClick={unmarkHouse}>
                Retirer maison
              </Button>
            )}
            {!org.is_house && !org.charges_enabled && (
              <Button size="sm" onClick={onboard} data-testid="stripe-onboard" className="bg-[#E5276A] hover:bg-[#C01661]">
                Activer Stripe Connect
              </Button>
            )}
            {!org.is_house && (
              <Button size="sm" variant="outline" onClick={refreshStripe}>Vérifier</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mt-6 mb-3">
        <h2 className="text-lg font-semibold">Événements</h2>
        <Button size="sm" data-testid="new-event-btn" onClick={() => setShowNew(true)} className="bg-[#E5276A] hover:bg-[#C01661]">
          <Plus className="w-4 h-4 mr-1" /> Nouveau
        </Button>
      </div>
      {showNew && (
        <Card className="mb-3"><CardContent className="p-4 grid gap-2">
          <Input data-testid="event-name" placeholder="Nom de l'événement" value={evName} onChange={(e) => setEvName(e.target.value)} />
          <Input data-testid="event-organizer" placeholder="Nom de l'organisateur affiché (email/tickets)"
                 value={organizerName} onChange={(e) => setOrganizerName(e.target.value)} />
          <Input data-testid="event-address" placeholder="Adresse (ex : 10 place République, Paris)"
                 value={address} onChange={(e) => setAddress(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-neutral-500">Début</label>
              <Input data-testid="event-starts" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Fin</label>
              <Input data-testid="event-ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm mt-1">
            <input type="checkbox" checked={enableSlots}
                   onChange={(e) => setEnableSlots(e.target.checked)}
                   data-testid="event-enable-slots" />
            Proposer des créneaux (les participants choisiront à l'inscription)
          </label>
          {enableSlots && (
            <div className="grid grid-cols-2 gap-2 pl-6">
              <div>
                <label className="text-xs text-neutral-500">Durée (min)</label>
                <Input type="number" value={slotDuration} onChange={(e) => setSlotDuration(parseInt(e.target.value) || 60)} data-testid="event-slot-duration" />
              </div>
              <div>
                <label className="text-xs text-neutral-500">Capacité / créneau</label>
                <Input type="number" value={slotCapacity} onChange={(e) => setSlotCapacity(parseInt(e.target.value) || 20)} data-testid="event-slot-capacity" />
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Button onClick={createEvent} data-testid="create-event-submit" className="bg-[#E5276A] hover:bg-[#C01661]">Créer</Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Annuler</Button>
          </div>
        </CardContent></Card>
      )}
      <div className="grid gap-2">
        {events.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Aucun événement.</p>}
        {events.map(e => (
          <Card key={e.id} className="hover:border-[#E5276A] transition"
                data-testid={`event-row-${e.id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <Link to={`/pro/experience/events/${e.id}`} className="flex-1 flex items-center gap-3 min-w-0">
                <Calendar className="w-5 h-5 text-[#E5276A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{e.name}</div>
                  <div className="text-xs text-neutral-500">
                    {formatParisDate(e.starts_at)}
                    · <span className={
                      e.status === "published" ? "text-emerald-600" :
                      e.status === "live" ? "text-blue-600" : "text-neutral-500"
                    }>{e.status}</span>
                  </div>
                </div>
              </Link>
              <Link to={`/pro/experience/events/${e.id}`}
                    className="p-1.5 rounded-md text-neutral-500 hover:text-[#E5276A] hover:bg-neutral-100"
                    title="Modifier">
                <Pencil className="w-4 h-4" />
              </Link>
              <button
                data-testid={`delete-event-${e.id}`}
                onClick={async (ev) => {
                  ev.preventDefault();
                  if (!window.confirm(`Supprimer "${e.name}" ?`)) return;
                  try {
                    await proXpApi.deleteEvent(e.id);
                    toast.success("Supprimé"); load();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Impossible"); }
                }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-red-500 hover:bg-red-50"
                title="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
              <ArrowRight className="w-4 h-4 text-neutral-400" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
